import {
  Search,
  Globe,
  Megaphone,
  Share2,
  ShieldCheck,
  MousePointerClick,
  Video,
  Palette,
  Mail,
  MessageCircle,
  BarChart3,
  TrendingUp,
  Target,
  LineChart,
  MapPinned,
  FileText,
  Paintbrush,
  Bot,
} from "lucide-react";

import type { ServicePage } from "@/types/service";

export const digitalMarketingSolutions: ServicePage[] = [
  // Seo Services
  {
    title: "SEO Services",
    slug: "seo-services",
    group: "Digital Marketing Solutions",
    icon: Search,

    seo: {
      title: "SEO Services",
      description:
        "Improve your search engine rankings and drive qualified organic traffic with result-oriented SEO services from Skand Tech Hub.",
      keywords: [
        "SEO Services",
        "Search Engine Optimization",
        "Technical SEO",
        "Local SEO",
        "Enterprise SEO",
        "SEO Company",
      ],
    },

    hero: {
      title: "SEO Services That Drive Organic Growth",
      description:
        "Increase your online visibility, attract high-intent visitors, and generate consistent leads with data-driven SEO strategies tailored to your business goals.",
      highlights: [
        "Technical SEO Audits",
        "Keyword Strategy",
        "On-Page Optimization",
        "Off-Page SEO",
      ],
    },

    whyChoose: {
      title: "Why Invest in SEO?",
      description:
        "SEO is one of the most sustainable digital marketing channels, helping businesses generate long-term traffic and qualified leads.",
      items: [
        {
          title: "Long-Term Organic Growth",
          description:
            "Generate consistent website traffic without relying entirely on paid advertising.",
          icon: TrendingUp,
        },
        {
          title: "Higher Search Rankings",
          description:
            "Improve your visibility on Google and other search engines for relevant keywords.",
          icon: Search,
        },
        {
          title: "Qualified Lead Generation",
          description:
            "Attract users actively searching for your products or services with high purchase intent.",
          icon: Target,
        },
        {
          title: "Measurable Performance",
          description:
            "Track rankings, traffic, conversions, and ROI using industry-leading analytics tools.",
          icon: BarChart3,
        },
      ],
    },

    services: {
      title: "Our SEO Services",
      description:
        "Comprehensive SEO solutions designed for startups, SMEs, and enterprise businesses.",
      items: [
        {
          title: "Technical SEO",
          description:
            "Identify and resolve technical issues affecting crawlability, indexing, Core Web Vitals, site speed, mobile usability, and overall search performance.",
          icon: Search,
          features: [
            "Technical Website Audit",
            "Core Web Vitals Optimization",
            "XML Sitemap & Robots.txt",
            "Schema Markup",
            "Broken Link Resolution",
          ],
        },
        {
          title: "On-Page SEO",
          description:
            "Optimize every important page with keyword-focused content, metadata, headings, internal linking, and structured content to improve search visibility.",
          icon: Target,
          features: [
            "Keyword Research",
            "Meta Title & Description",
            "Heading Structure",
            "Content Optimization",
            "Internal Linking",
          ],
        },
        {
          title: "Off-Page SEO",
          description:
            "Build your website's authority through ethical link-building strategies, digital PR, and high-quality backlink acquisition.",
          icon: TrendingUp,
          features: [
            "Authority Backlinks",
            "Guest Posting",
            "Digital PR",
            "Brand Mentions",
            "Competitor Link Analysis",
          ],
        },
        {
          title: "Local SEO",
          description:
            "Increase visibility in local searches by optimizing your Google Business Profile, local citations, maps presence, and location-based keywords.",
          icon: MapPinned,
          features: [
            "Google Business Profile",
            "Local Citation Building",
            "Location Landing Pages",
            "Review Management",
            "Maps Optimization",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Everything required to build a strong organic search presence.",
      items: [
        {
          title: "Comprehensive SEO Audit",
          description:
            "Analyze technical issues, content quality, user experience, and website health to identify opportunities for improvement.",
          icon: Search,
        },
        {
          title: "Keyword & Competitor Research",
          description:
            "Discover high-value keywords, analyze competitors, and build a data-driven SEO strategy that targets qualified traffic.",
          icon: Target,
        },
        {
          title: "Content Optimization",
          description:
            "Optimize existing pages and create search-focused content that aligns with user intent and improves rankings.",
          icon: FileText,
        },
        {
          title: "Performance Monitoring",
          description:
            "Track keyword rankings, organic traffic, conversions, and technical health with detailed reporting and continuous optimization.",
          icon: LineChart,
        },
      ],
    },

    process: {
      title: "Our SEO Process",
      description: "A structured workflow focused on measurable growth.",
      items: [
        {
          title: "Website & Business Analysis",
          description:
            "Understand your business goals, target audience, competitors, and current website performance to build a tailored SEO strategy.",
        },
        {
          title: "Technical SEO Audit",
          description:
            "Perform a detailed audit to identify crawl errors, indexing issues, Core Web Vitals, mobile usability, and technical bottlenecks.",
        },
        {
          title: "Keyword & Content Strategy",
          description:
            "Research high-intent keywords, map them to relevant pages, and create a content strategy aligned with user search intent.",
        },
        {
          title: "Optimization & Implementation",
          description:
            "Optimize on-page elements, improve site architecture, implement schema markup, strengthen internal linking, and resolve technical issues.",
        },
        {
          title: "Authority Building",
          description:
            "Execute ethical off-page SEO through quality backlink acquisition, digital PR, local citations, and brand authority initiatives.",
        },
        {
          title: "Monitoring & Continuous Improvement",
          description:
            "Track rankings, traffic, conversions, and user behavior while continuously refining the strategy for sustained organic growth.",
        },
      ],
    },

    portfolio: {
      title: "SEO Success Stories",
      description:
        "Projects focused on improving visibility, traffic, and conversions.",
      items: [
        {
          title: "Local Business SEO",
          description:
            "Improved local visibility and increased qualified enquiries through Google Business Profile optimization and location-based SEO.",
          image: "/project-image.webp",
        },
        {
          title: "E-Commerce Growth",
          description:
            "Enhanced organic traffic and product discoverability through technical SEO, category optimization, and structured data.",
          image: "/project-image.webp",
        },
        {
          title: "Corporate Website Optimization",
          description:
            "Strengthened domain authority, search rankings, and lead generation with a comprehensive enterprise SEO strategy.",
          image: "/project-image.webp",
        },
      ],
    },

    benefits: {
      title: "Benefits of SEO",
      description:
        "Achieve sustainable growth with a long-term search strategy.",
      items: [
        {
          title: "Sustainable Organic Traffic",
          description:
            "Build a consistent flow of visitors from search engines without relying solely on paid advertising.",
          icon: TrendingUp,
        },
        {
          title: "Higher Quality Leads",
          description:
            "Reach users actively searching for your products or services, resulting in better conversion opportunities.",
          icon: Target,
        },
        {
          title: "Improved Brand Credibility",
          description:
            "Appearing prominently in search results increases trust, authority, and brand recognition among potential customers.",
          icon: ShieldCheck,
        },
        {
          title: "Better User Experience",
          description:
            "SEO improvements such as faster loading pages, mobile optimization, and better navigation enhance the overall user experience.",
          icon: Globe,
        },
        {
          title: "Long-Term Return on Investment",
          description:
            "Unlike paid campaigns that stop generating traffic when the budget ends, SEO continues delivering value over time.",
          icon: BarChart3,
        },
        {
          title: "Actionable Business Insights",
          description:
            "Leverage search data, user behaviour, and analytics to make informed marketing and business decisions.",
          icon: LineChart,
        },
      ],
    },

    techStack: {
      title: "SEO Tools & Platforms",
      description:
        "Industry-leading tools used for research, auditing, and performance tracking.",
      technologies: [
        "Google Search Console",
        "Google Analytics",
        "Google Tag Manager",
        "Ahrefs",
        "SEMrush",
        "Screaming Frog",
        "PageSpeed Insights",
        "Google Business Profile",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine technical expertise with proven SEO methodologies to deliver measurable business results.",
      items: [
        {
          title: "Experienced SEO Specialists",
          description:
            "Our team combines technical SEO expertise, content strategy, and analytics to build campaigns focused on measurable business growth.",
          icon: Search,
        },
        {
          title: "Data-Driven Strategy",
          description:
            "Every recommendation is backed by keyword research, competitor analysis, and performance metrics instead of assumptions.",
          icon: BarChart3,
        },
        {
          title: "Transparent Reporting",
          description:
            "Receive regular reports with keyword rankings, organic traffic insights, technical improvements, and actionable recommendations.",
          icon: LineChart,
        },
        {
          title: "Ethical White-Hat SEO",
          description:
            "We follow search engine best practices and sustainable optimization techniques that build long-term authority.",
          icon: ShieldCheck,
        },
        {
          title: "Business-Focused Execution",
          description:
            "Our objective isn't just better rankings—it's generating qualified leads, increasing conversions, and supporting your business goals.",
          icon: Target,
        },
        {
          title: "Continuous Optimization",
          description:
            "SEO is an ongoing process. We continuously monitor performance, adapt strategies, and capitalize on new opportunities as algorithms evolve.",
          icon: TrendingUp,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "SEO strategies customised for different industries and business models.",
      items: [
        {
          title: "Local Businesses",
          description:
            "Improve local search visibility, Google Business Profile rankings, and location-based customer acquisition.",
          icon: Globe,
        },
        {
          title: "E-Commerce Stores",
          description:
            "Increase product visibility, category rankings, and organic sales through technical and content-focused SEO.",
          icon: TrendingUp,
        },
        {
          title: "Healthcare",
          description:
            "Build trust and attract patients with compliant, location-focused, and informative healthcare SEO strategies.",
          icon: ShieldCheck,
        },
        {
          title: "Education",
          description:
            "Help schools, colleges, and EdTech platforms attract students with search-optimized educational content.",
          icon: Target,
        },
        {
          title: "Real Estate",
          description:
            "Generate high-quality property inquiries by ranking for local and high-intent real estate searches.",
          icon: Search,
        },
        {
          title: "SaaS & Technology",
          description:
            "Increase product awareness, trial sign-ups, and inbound leads through scalable B2B SEO strategies.",
          icon: BarChart3,
        },
      ],
    },

    hire: {
      title: "Hire SEO Experts",
      description:
        "Whether you're launching a new website or improving an existing one, our SEO specialists create tailored strategies that increase visibility, generate qualified traffic, and support long-term business growth.",
      cta: "Let's Grow Your Organic Traffic",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to improve their online visibility and generate measurable organic growth.",
      items: [
        {
          name: "Michael Carter",
          company: "GrowthEdge Solutions",
          location: "United States",
          review:
            "Skand Tech Hub significantly improved our search visibility and organic lead generation. Their technical expertise and transparent reporting made the entire process seamless.",
        },
        {
          name: "Sarah Williams",
          company: "UrbanMart",
          location: "United Kingdom",
          review:
            "Our organic traffic and keyword rankings improved consistently after partnering with their SEO team. Highly professional and results-driven.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      items: [
        {
          question: "How long does SEO take to show results?",
          answer:
            "SEO is a long-term strategy. While minor improvements can be seen within a few weeks, meaningful ranking and traffic growth typically takes 3–6 months depending on competition and website condition.",
        },
        {
          question: "Do you provide Local SEO services?",
          answer:
            "Yes. We optimize Google Business Profiles, local citations, location pages, and local keywords to improve visibility in regional search results.",
        },
        {
          question: "Can you optimize an existing website?",
          answer:
            "Absolutely. We perform a complete SEO audit, identify technical and content issues, and optimize your existing website without requiring a rebuild.",
        },
        {
          question: "Do you guarantee first-page rankings?",
          answer:
            "No ethical SEO company can guarantee specific rankings. We follow proven, search-engine-approved practices focused on sustainable growth and measurable business outcomes.",
        },
        {
          question: "Do you provide monthly SEO reports?",
          answer:
            "Yes. We share detailed reports covering keyword rankings, organic traffic, technical improvements, and recommendations for continued growth.",
        },
      ],
    },
  },
  // Smo Services
  {
    title: "Social Media Optimization",
    slug: "social-media-optimization",
    group: "Digital Marketing Solutions",
    icon: Share2,

    seo: {
      title: "Social Media Optimization Services",
      description:
        "Build a stronger brand presence with professional Social Media Optimization (SMO) services that increase engagement, improve visibility, and grow your online community organically.",
      keywords: [
        "Social Media Optimization",
        "SMO Services",
        "Organic Social Media Growth",
        "Social Media Management",
        "Brand Engagement",
        "Social Media Company",
      ],
    },

    hero: {
      title: "Social Media Optimization That Builds Brand Presence",
      description:
        "Strengthen your brand across leading social media platforms with strategic profile optimization, engaging content, and community-focused growth that builds trust and long-term customer relationships.",
      highlights: [
        "Profile Optimization",
        "Content Strategy",
        "Community Engagement",
        "Performance Analytics",
      ],
    },

    whyChoose: {
      title: "Why Invest in Social Media Optimization?",
      description:
        "SMO helps businesses establish credibility, improve engagement, and create meaningful connections with their audience through consistent organic growth.",
      items: [
        {
          title: "Increase Brand Visibility",
          description:
            "Build a recognizable and consistent brand presence across multiple social media platforms.",
          icon: TrendingUp,
        },
        {
          title: "Boost Audience Engagement",
          description:
            "Encourage meaningful interactions through optimized content and community management.",
          icon: MessageCircle,
        },
        {
          title: "Build Customer Trust",
          description:
            "Create credibility with professionally managed profiles and consistent communication.",
          icon: ShieldCheck,
        },
        {
          title: "Support Long-Term Growth",
          description:
            "Develop sustainable organic reach without depending entirely on paid advertising.",
          icon: BarChart3,
        },
      ],
    },

    services: {
      title: "Our SMO Services",
      description:
        "We optimize every aspect of your social presence to improve reach, engagement, and brand awareness.",
      items: [
        {
          title: "Profile Optimization",
          description:
            "Optimize business profiles, branding, descriptions, contact details, and platform settings.",
          features: [
            "Profile Branding",
            "Bio Optimization",
            "Business Information",
            "Visual Consistency",
            "Call-to-Action Setup",
          ],
          icon: Share2,
        },
        {
          title: "Content Strategy",
          description:
            "Develop engaging content calendars tailored to your audience and business objectives.",
          features: [
            "Content Planning",
            "Creative Ideas",
            "Hashtag Strategy",
            "Posting Schedule",
            "Content Mix",
          ],
          icon: FileText,
        },
        {
          title: "Community Engagement",
          description:
            "Strengthen relationships through audience interaction and community management.",
          features: [
            "Comment Management",
            "Message Responses",
            "Audience Engagement",
            "Community Building",
            "Brand Interaction",
          ],
          icon: MessageCircle,
        },
        {
          title: "Performance Optimization",
          description:
            "Continuously improve content performance using analytics and audience insights.",
          features: [
            "Performance Reports",
            "Engagement Analysis",
            "Growth Tracking",
            "Content Optimization",
            "Strategy Refinement",
          ],
          icon: LineChart,
        },
      ],
    },

    features: {
      title: "What's Included",
      description:
        "Everything required to strengthen your organic social media presence.",
      items: [
        {
          title: "Social Profile Audit",
          description:
            "Comprehensive evaluation of your existing social media profiles.",
          icon: Search,
        },
        {
          title: "Content Planning",
          description:
            "Monthly content calendars designed around your audience and goals.",
          icon: FileText,
        },
        {
          title: "Brand Consistency",
          description:
            "Maintain a unified brand identity across all social platforms.",
          icon: Palette,
        },
        {
          title: "Analytics & Reporting",
          description:
            "Track growth, engagement, and audience behaviour with detailed reports.",
          icon: LineChart,
        },
      ],
    },

    process: {
      title: "Our SMO Process",
      description:
        "A structured approach to building a stronger social media presence.",
      items: [
        {
          title: "Profile Audit",
          description:
            "Analyze existing profiles, branding, competitors, and audience.",
        },
        {
          title: "Strategy Development",
          description:
            "Create a customized content and engagement strategy aligned with business objectives.",
        },
        {
          title: "Profile Optimization",
          description:
            "Optimize visuals, bios, descriptions, and account settings.",
        },
        {
          title: "Content Execution",
          description:
            "Publish engaging content consistently across selected platforms.",
        },
        {
          title: "Community Engagement",
          description:
            "Interact with followers and build meaningful customer relationships.",
        },
        {
          title: "Performance Monitoring",
          description:
            "Measure engagement, reach, and growth while refining future strategies.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Social Media Optimization",
      description:
        "Grow a stronger online brand with sustainable social media strategies.",
      items: [
        {
          title: "Greater Brand Awareness",
          description:
            "Reach more potential customers through consistent social visibility.",
          icon: TrendingUp,
        },
        {
          title: "Higher Engagement",
          description:
            "Increase likes, comments, shares, and meaningful audience interactions.",
          icon: MessageCircle,
        },
        {
          title: "Improved Customer Trust",
          description:
            "Build credibility through an active and professional social presence.",
          icon: ShieldCheck,
        },
        {
          title: "Organic Community Growth",
          description:
            "Grow genuine followers interested in your products and services.",
          icon: Target,
        },
        {
          title: "Better Customer Relationships",
          description:
            "Strengthen long-term relationships through continuous engagement.",
          icon: Globe,
        },
        {
          title: "Actionable Insights",
          description:
            "Understand audience behaviour and improve future campaigns.",
          icon: BarChart3,
        },
      ],
    },

    techStack: {
      title: "Platforms We Optimize",
      description:
        "Industry-leading tools used for research, auditing, and performance tracking.",
      technologies: [
        "Facebook",
        "Instagram",
        "LinkedIn",
        "X (Twitter)",
        "Pinterest",
        "YouTube",
        "TikTok",
        "Threads",
        "Google Business Profile",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine creativity, strategy, and analytics to help businesses build a strong organic social presence.",
      items: [
        {
          title: "Creative Content Strategy",
          description:
            "Content designed to educate, engage, and convert your audience.",
          icon: Palette,
        },
        {
          title: "Platform Expertise",
          description:
            "Experience optimizing brands across all major social media platforms.",
          icon: Share2,
        },
        {
          title: "Data-Driven Decisions",
          description:
            "Every optimization is backed by analytics and audience insights.",
          icon: LineChart,
        },
        {
          title: "Consistent Brand Voice",
          description:
            "Maintain a professional and recognizable identity across every platform.",
          icon: ShieldCheck,
        },
        {
          title: "Transparent Reporting",
          description:
            "Receive regular reports with measurable growth metrics.",
          icon: BarChart3,
        },
        {
          title: "Long-Term Growth Focus",
          description:
            "Our strategies are built for sustainable community growth rather than short-term spikes.",
          icon: TrendingUp,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "Helping businesses across industries build stronger social media communities.",
      items: [
        {
          title: "Healthcare",
          description:
            "Build patient trust and improve healthcare brand awareness.",
          icon: ShieldCheck,
        },
        {
          title: "Education",
          description:
            "Increase student engagement and institutional visibility.",
          icon: Target,
        },
        {
          title: "Real Estate",
          description:
            "Showcase properties and generate quality buyer enquiries.",
          icon: Globe,
        },
        {
          title: "Retail & E-Commerce",
          description: "Promote products and strengthen customer loyalty.",
          icon: TrendingUp,
        },
        {
          title: "Hospitality",
          description: "Attract guests through engaging visual storytelling.",
          icon: MessageCircle,
        },
        {
          title: "Technology & SaaS",
          description: "Build authority and engage modern digital audiences.",
          icon: BarChart3,
        },
      ],
    },

    portfolio: {
      title: "Recent SMO Success Stories",
      description:
        "Examples of how we help businesses grow their social media presence.",
      items: [
        {
          title: "Retail Brand Growth",
          description:
            "Improved engagement and organic follower growth through a structured content strategy.",
          image: "/project-image.webp",
        },
        {
          title: "Healthcare Social Presence",
          description:
            "Enhanced brand trust and audience interaction with consistent educational content.",
          image: "/project-image.webp",
        },
        {
          title: "B2B LinkedIn Optimization",
          description:
            "Strengthened professional visibility and generated quality business enquiries.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire SMO Experts",
      description:
        "Partner with our Social Media Optimization specialists to build a stronger brand, engage your audience, and achieve sustainable organic growth.",
      cta: "Let's Optimize Your Social Presence",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to strengthen their social presence and community engagement.",
      items: [
        {
          name: "Emily Johnson",
          company: "Bright Retail",
          location: "United States",
          review:
            "Our engagement and follower quality improved dramatically after working with Skand Tech Hub. Their content strategy has been exceptional.",
        },
        {
          name: "Daniel Smith",
          company: "EduSphere",
          location: "Australia",
          review:
            "Their SMO team transformed our social media presence with consistent branding and measurable audience growth.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our Social Media Optimization services.",
      items: [
        {
          question: "What's the difference between SMO and SMM?",
          answer:
            "SMO focuses on improving your organic social media presence through profile optimization, content strategy, and community engagement, while SMM includes paid advertising campaigns.",
        },
        {
          question: "Which social media platforms do you support?",
          answer:
            "We optimize Facebook, Instagram, LinkedIn, X (Twitter), Pinterest, YouTube, TikTok, Threads, and Google Business Profile.",
        },
        {
          question: "How long does SMO take to show results?",
          answer:
            "Organic growth typically becomes noticeable within a few weeks, with significant improvements in engagement and reach developing over several months of consistent optimization.",
        },
        {
          question: "Do you create content as well?",
          answer:
            "Yes. We assist with content planning, captions, creative ideas, hashtag strategies, and publishing schedules.",
        },
        {
          question: "Do you provide monthly reports?",
          answer:
            "Absolutely. We share detailed reports covering audience growth, engagement, reach, content performance, and recommendations.",
        },
      ],
    },
  },
  // Smm Services
  {
    title: "Social Media Marketing",
    slug: "social-media-marketing",
    group: "Digital Marketing Solutions",
    icon: Megaphone,

    seo: {
      title: "Social Media Marketing Services",
      description:
        "Grow your business with result-driven Social Media Marketing (SMM) services. We create high-performing paid campaigns that increase brand awareness, generate qualified leads, and maximize ROI.",
      keywords: [
        "Social Media Marketing",
        "SMM Services",
        "Facebook Ads",
        "Instagram Ads",
        "LinkedIn Marketing",
        "Paid Social Media Campaigns",
      ],
    },

    hero: {
      title: "Social Media Marketing That Drives Real Business Growth",
      description:
        "Reach the right audience, generate quality leads, and maximize your return on investment with strategic social media advertising campaigns across leading digital platforms.",
      highlights: [
        "Paid Advertising",
        "Audience Targeting",
        "Lead Generation",
        "Campaign Optimization",
      ],
    },

    whyChoose: {
      title: "Why Invest in Social Media Marketing?",
      description:
        "Social Media Marketing enables businesses to reach highly targeted audiences, generate measurable results, and scale customer acquisition through strategic paid campaigns.",
      items: [
        {
          title: "Reach Your Ideal Audience",
          description:
            "Target customers based on demographics, interests, behaviour, and purchasing intent.",
          icon: Target,
        },
        {
          title: "Generate Qualified Leads",
          description:
            "Drive high-quality enquiries and conversions with optimized advertising campaigns.",
          icon: TrendingUp,
        },
        {
          title: "Increase Brand Awareness",
          description:
            "Build visibility and recognition across the world's most popular social media platforms.",
          icon: Globe,
        },
        {
          title: "Measurable ROI",
          description:
            "Track campaign performance with real-time analytics and actionable insights.",
          icon: BarChart3,
        },
      ],
    },

    services: {
      title: "Our Social Media Marketing Services",
      description:
        "End-to-end social media advertising solutions designed to maximize performance and business growth.",
      items: [
        {
          title: "Paid Advertising Campaigns",
          description:
            "Launch high-converting advertising campaigns across multiple social media platforms.",
          icon: Megaphone,
          features: [
            "Facebook Ads",
            "Instagram Ads",
            "LinkedIn Ads",
            "TikTok Ads",
            "Pinterest Ads",
          ],
        },
        {
          title: "Audience Targeting",
          description:
            "Reach the right audience using advanced targeting, segmentation, and behavioural insights.",
          icon: Target,
          features: [
            "Interest Targeting",
            "Custom Audiences",
            "Lookalike Audiences",
            "Geo Targeting",
            "Behaviour Targeting",
          ],
        },
        {
          title: "Creative Ad Management",
          description:
            "Develop engaging creatives and compelling ad copy designed to improve campaign performance.",
          icon: Palette,
          features: [
            "Ad Creatives",
            "Copywriting",
            "Carousel Ads",
            "Video Ads",
            "A/B Testing",
          ],
        },
        {
          title: "Campaign Optimization",
          description:
            "Continuously monitor, analyze, and optimize campaigns to improve conversions and reduce advertising costs.",
          icon: LineChart,
          features: [
            "Performance Monitoring",
            "Budget Optimization",
            "Conversion Tracking",
            "Bid Management",
            "ROI Optimization",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Everything required to build successful paid social media campaigns.",
      items: [
        {
          title: "Audience Research",
          description:
            "Identify and target the most relevant audience segments for your business objectives.",
          icon: Search,
        },
        {
          title: "Creative Campaign Design",
          description:
            "Design compelling advertisements that capture attention and encourage action.",
          icon: Palette,
        },
        {
          title: "Conversion Tracking",
          description:
            "Measure every click, lead, and conversion with accurate tracking implementation.",
          icon: Target,
        },
        {
          title: "Performance Reporting",
          description:
            "Receive detailed campaign reports with actionable recommendations for continuous improvement.",
          icon: LineChart,
        },
      ],
    },

    process: {
      title: "Our Social Media Marketing Process",
      description:
        "A structured advertising workflow focused on maximizing return on investment.",
      items: [
        {
          title: "Business & Campaign Discovery",
          description:
            "Understand your goals, audience, competitors, and marketing objectives.",
        },
        {
          title: "Campaign Strategy",
          description:
            "Develop a customized advertising strategy based on your budget and business goals.",
        },
        {
          title: "Creative Development",
          description:
            "Design high-performing ad creatives, compelling copy, and engaging campaign assets.",
        },
        {
          title: "Campaign Launch",
          description:
            "Deploy optimized campaigns across selected social media advertising platforms.",
        },
        {
          title: "Optimization & Testing",
          description:
            "Continuously optimize targeting, creatives, bidding, and campaign performance through A/B testing.",
        },
        {
          title: "Reporting & Scaling",
          description:
            "Analyze campaign performance, identify opportunities, and scale successful campaigns for maximum ROI.",
        },
      ],
    },
    benefits: {
      title: "Benefits of Social Media Marketing",
      description:
        "Accelerate business growth with data-driven social media advertising campaigns.",
      items: [
        {
          title: "Instant Brand Visibility",
          description:
            "Reach thousands of potential customers quickly through targeted advertising campaigns.",
          icon: TrendingUp,
        },
        {
          title: "Qualified Lead Generation",
          description:
            "Generate high-quality leads by targeting audiences most likely to convert.",
          icon: Target,
        },
        {
          title: "Higher Conversion Rates",
          description:
            "Deliver personalized advertisements that encourage users to take action.",
          icon: BarChart3,
        },
        {
          title: "Scalable Advertising",
          description:
            "Increase campaign budgets confidently while maintaining performance and ROI.",
          icon: Globe,
        },
        {
          title: "Cost-Effective Marketing",
          description:
            "Optimize advertising spend with continuous monitoring and campaign improvements.",
          icon: ShieldCheck,
        },
        {
          title: "Real-Time Insights",
          description:
            "Monitor campaign performance instantly and make informed marketing decisions.",
          icon: LineChart,
        },
      ],
    },

    techStack: {
      title: "Advertising Platforms",
      description:
        "Industry-leading tools used for research, auditing, and performance tracking.",
      technologies: [
        "Facebook Ads Manager",
        "Instagram Ads",
        "LinkedIn Campaign Manager",
        "TikTok Ads",
        "Pinterest Ads",
        "Meta Business Suite",
        "Google Analytics",
        "Meta Pixel",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine creativity, audience insights, and performance marketing expertise to deliver profitable advertising campaigns.",
      items: [
        {
          title: "Performance-Driven Campaigns",
          description:
            "Every campaign is optimized to achieve measurable business goals and maximize ROI.",
          icon: TrendingUp,
        },
        {
          title: "Creative Advertising",
          description:
            "We create compelling visuals and ad copy that capture attention and drive engagement.",
          icon: Palette,
        },
        {
          title: "Advanced Audience Targeting",
          description:
            "Reach the right customers using intelligent audience segmentation and behavioural targeting.",
          icon: Target,
        },
        {
          title: "Transparent Reporting",
          description:
            "Receive detailed reports with campaign performance, advertising spend, and conversion insights.",
          icon: LineChart,
        },
        {
          title: "Continuous Optimization",
          description:
            "We monitor campaigns daily and refine strategies to improve results over time.",
          icon: BarChart3,
        },
        {
          title: "Experienced Marketing Team",
          description:
            "Our specialists stay updated with platform changes and advertising best practices.",
          icon: ShieldCheck,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "Social media advertising solutions tailored for businesses across multiple industries.",
      items: [
        {
          title: "E-Commerce",
          description:
            "Increase online sales with highly targeted shopping and conversion campaigns.",
          icon: TrendingUp,
        },
        {
          title: "Healthcare",
          description:
            "Generate patient enquiries while maintaining professional brand credibility.",
          icon: ShieldCheck,
        },
        {
          title: "Education",
          description:
            "Reach prospective students through strategic awareness and lead generation campaigns.",
          icon: Target,
        },
        {
          title: "Real Estate",
          description:
            "Promote properties and generate qualified buyer and investor leads.",
          icon: Globe,
        },
        {
          title: "Hospitality",
          description:
            "Increase bookings through visually engaging travel and hospitality campaigns.",
          icon: Megaphone,
        },
        {
          title: "Technology & SaaS",
          description:
            "Generate product awareness, demo requests, and qualified B2B leads.",
          icon: BarChart3,
        },
      ],
    },

    portfolio: {
      title: "Recent SMM Success Stories",
      description:
        "Campaigns focused on increasing reach, leads, and return on advertising investment.",
      items: [
        {
          title: "E-Commerce Sales Campaign",
          description:
            "Generated consistent online sales growth through optimized Meta advertising campaigns.",
          image: "/project-image.webp",
        },
        {
          title: "Real Estate Lead Generation",
          description:
            "Delivered high-quality property enquiries using location-focused social media advertisements.",
          image: "/project-image.webp",
        },
        {
          title: "SaaS Brand Awareness",
          description:
            "Increased product visibility and demo requests through LinkedIn and Meta advertising.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Social Media Marketing Experts",
      description:
        "Partner with our SMM specialists to launch high-performing advertising campaigns that increase brand awareness, generate qualified leads, and maximize your marketing ROI.",
      cta: "Let's Grow Your Business",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to create and manage high-performing social media advertising campaigns.",
      items: [
        {
          name: "James Walker",
          company: "RetailX",
          location: "United States",
          review:
            "Their advertising strategy significantly improved our lead quality while reducing our cost per acquisition. Excellent campaign management.",
        },
        {
          name: "Sophia Brown",
          company: "Property Connect",
          location: "Australia",
          review:
            "The team delivered measurable results through optimized social media campaigns. We saw consistent lead growth month after month.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      items: [
        {
          question: "What's the difference between SMO and SMM?",
          answer:
            "SMO focuses on building your organic social media presence, while SMM uses paid advertising campaigns to generate faster visibility, leads, and conversions.",
        },
        {
          question: "Which advertising platforms do you manage?",
          answer:
            "We manage advertising campaigns across Facebook, Instagram, LinkedIn, TikTok, Pinterest, and other leading social media platforms.",
        },
        {
          question: "How soon can I expect results?",
          answer:
            "Paid campaigns typically begin generating impressions, clicks, and leads shortly after launch, while ongoing optimization improves results over time.",
        },
        {
          question: "Do you create ad creatives?",
          answer:
            "Yes. We design ad creatives, write compelling copy, and optimize campaigns for maximum performance.",
        },
        {
          question: "Do you provide campaign reports?",
          answer:
            "Yes. We provide detailed reports covering campaign performance, advertising spend, conversions, audience insights, and optimization recommendations.",
        },
      ],
    },
  },
  // Orm Services
  {
    title: "ORM Services",
    slug: "orm-services",
    group: "Digital Marketing Solutions",
    icon: ShieldCheck,

    seo: {
      title: "Online Reputation Management Services",
      description:
        "Protect and strengthen your brand reputation with professional ORM services covering reputation monitoring, review management, brand protection, and customer engagement.",
      keywords: [
        "ORM Services",
        "Online Reputation Management",
        "Reputation Management Services",
        "Review Management",
        "Brand Reputation Management",
        "Online Reputation Management Company",
      ],
    },

    hero: {
      title: "Online Reputation Management That Builds Trust",
      description:
        "Protect your brand, strengthen customer confidence, and maintain a positive digital presence with proactive reputation monitoring, review management, and strategic customer engagement.",
      highlights: [
        "Reputation Monitoring",
        "Review Management",
        "Brand Protection",
        "Customer Trust",
      ],
    },

    whyChoose: {
      title: "Why Invest in Online Reputation Management?",
      description:
        "Your online reputation directly influences customer trust, purchasing decisions, and brand credibility. Strategic ORM helps businesses protect and strengthen that reputation.",
      items: [
        {
          title: "Build Customer Trust",
          description:
            "Strengthen credibility by maintaining a professional and positive brand presence across digital channels.",
          icon: ShieldCheck,
        },
        {
          title: "Protect Brand Reputation",
          description:
            "Identify reputation risks early and respond strategically before they negatively impact your brand.",
          icon: Search,
        },
        {
          title: "Improve Customer Perception",
          description:
            "Encourage positive customer experiences and strengthen how audiences perceive your business online.",
          icon: TrendingUp,
        },
        {
          title: "Make Reputation Measurable",
          description:
            "Track reviews, sentiment, brand mentions, and reputation trends using actionable insights.",
          icon: BarChart3,
        },
      ],
    },

    services: {
      title: "Our ORM Services",
      description:
        "Comprehensive reputation management solutions designed to monitor, protect, and strengthen your brand across digital platforms.",
      items: [
        {
          title: "Online Reputation Monitoring",
          description:
            "Monitor brand mentions, customer conversations, reviews, and reputation signals across relevant online channels.",
          icon: Search,
          features: [
            "Brand Mention Monitoring",
            "Review Tracking",
            "Sentiment Monitoring",
            "Social Media Monitoring",
            "Reputation Alerts",
          ],
        },
        {
          title: "Review Management",
          description:
            "Manage customer reviews professionally while creating processes that encourage stronger customer feedback.",
          icon: MessageCircle,
          features: [
            "Review Monitoring",
            "Response Management",
            "Feedback Strategy",
            "Review Generation",
            "Customer Engagement",
          ],
        },
        {
          title: "Brand Reputation Protection",
          description:
            "Identify potential reputation threats and develop strategies to protect brand credibility across digital channels.",
          icon: ShieldCheck,
          features: [
            "Reputation Risk Analysis",
            "Negative Mention Tracking",
            "Response Strategy",
            "Brand Protection",
            "Crisis Support",
          ],
        },
        {
          title: "Reputation Improvement",
          description:
            "Strengthen positive brand visibility through content, customer engagement, and reputation-building initiatives.",
          icon: TrendingUp,
          features: [
            "Positive Content Strategy",
            "Brand Authority",
            "Customer Advocacy",
            "Search Visibility",
            "Reputation Growth",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Everything required to monitor your digital reputation and maintain stronger customer confidence.",
      items: [
        {
          title: "Brand Monitoring",
          description:
            "Track conversations, mentions, reviews, and other reputation signals across important digital platforms.",
          icon: Search,
        },
        {
          title: "Sentiment Analysis",
          description:
            "Understand how customers perceive your brand by evaluating positive, neutral, and negative feedback.",
          icon: BarChart3,
        },
        {
          title: "Review Response Strategy",
          description:
            "Respond professionally to customer feedback while protecting brand credibility and relationships.",
          icon: MessageCircle,
        },
        {
          title: "Reputation Reporting",
          description:
            "Measure reputation trends, review performance, customer sentiment, and opportunities for improvement.",
          icon: LineChart,
        },
      ],
    },

    process: {
      title: "Our ORM Process",
      description:
        "A structured reputation management approach focused on monitoring, protection, engagement, and continuous improvement.",
      items: [
        {
          title: "Reputation Audit",
          description:
            "Analyze your current online reputation, reviews, search presence, customer sentiment, and brand mentions.",
        },
        {
          title: "Risk & Opportunity Analysis",
          description:
            "Identify reputation risks, negative patterns, positive opportunities, and platforms requiring attention.",
        },
        {
          title: "ORM Strategy",
          description:
            "Develop a customized reputation management plan based on your brand, audience, and business objectives.",
        },
        {
          title: "Review & Engagement Management",
          description:
            "Manage customer feedback, review responses, and brand conversations across relevant platforms.",
        },
        {
          title: "Brand Protection & Improvement",
          description:
            "Strengthen positive visibility while addressing reputation issues through strategic content and engagement.",
        },
        {
          title: "Monitoring & Reporting",
          description:
            "Continuously track reputation signals and refine the strategy using customer sentiment and performance data.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Online Reputation Management",
      description:
        "Create a stronger digital reputation that supports customer confidence, credibility, and long-term business growth.",
      items: [
        {
          title: "Stronger Brand Credibility",
          description:
            "Build a trustworthy digital presence that gives potential customers greater confidence in your business.",
          icon: ShieldCheck,
        },
        {
          title: "Better Customer Relationships",
          description:
            "Show customers that their feedback matters through timely and professional engagement.",
          icon: MessageCircle,
        },
        {
          title: "Improved Brand Perception",
          description:
            "Strengthen positive customer sentiment and create a more credible image across digital channels.",
          icon: TrendingUp,
        },
        {
          title: "Early Risk Detection",
          description:
            "Identify negative conversations and emerging reputation issues before they develop into larger problems.",
          icon: Search,
        },
        {
          title: "Better Purchase Confidence",
          description:
            "Positive reviews and professional brand communication can strengthen customer confidence during buying decisions.",
          icon: Target,
        },
        {
          title: "Actionable Reputation Insights",
          description:
            "Use reviews and sentiment data to understand customer expectations and improve business decisions.",
          icon: BarChart3,
        },
      ],
    },

    techStack: {
      title: "ORM Platforms & Tools",
      description:
        "Platforms and monitoring tools used to track reviews, customer sentiment, brand mentions, and reputation performance.",
      technologies: [
        "Google Business Profile",
        "Trustpilot",
        "Facebook",
        "Instagram",
        "LinkedIn",
        "Google Alerts",
        "Social Listening Tools",
        "Google Analytics",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine reputation monitoring, strategic communication, analytics, and customer engagement to help businesses build stronger digital credibility.",
      items: [
        {
          title: "Proactive Monitoring",
          description:
            "Continuously monitor important reputation signals so potential issues can be identified early.",
          icon: Search,
        },
        {
          title: "Strategic Response Management",
          description:
            "Handle customer feedback professionally with responses aligned with your brand voice and reputation goals.",
          icon: MessageCircle,
        },
        {
          title: "Brand Protection Focus",
          description:
            "Develop strategies designed to protect credibility and reduce the impact of reputation risks.",
          icon: ShieldCheck,
        },
        {
          title: "Data-Driven Decisions",
          description:
            "Use review trends, sentiment, and reputation data to guide ongoing optimization.",
          icon: BarChart3,
        },
        {
          title: "Transparent Reporting",
          description:
            "Receive clear insights into reviews, customer sentiment, reputation trends, and ongoing activities.",
          icon: LineChart,
        },
        {
          title: "Long-Term Reputation Growth",
          description:
            "Focus on sustainable trust and credibility instead of temporary reputation fixes.",
          icon: TrendingUp,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "Reputation management strategies tailored to industries where customer trust and online credibility directly influence business growth.",
      items: [
        {
          title: "Healthcare",
          description:
            "Strengthen patient confidence through professional review management and reputation monitoring.",
          icon: ShieldCheck,
        },
        {
          title: "Hospitality",
          description:
            "Manage guest feedback and strengthen online reputation across review and social platforms.",
          icon: MessageCircle,
        },
        {
          title: "Real Estate",
          description:
            "Build credibility with buyers, sellers, and investors through stronger digital reputation management.",
          icon: Globe,
        },
        {
          title: "Education",
          description:
            "Protect institutional reputation and strengthen trust among students and parents.",
          icon: Target,
        },
        {
          title: "Retail & E-Commerce",
          description:
            "Manage product and customer feedback while strengthening buyer confidence and brand loyalty.",
          icon: TrendingUp,
        },
        {
          title: "Technology & SaaS",
          description:
            "Monitor customer sentiment and strengthen credibility among prospects and existing users.",
          icon: BarChart3,
        },
      ],
    },

    portfolio: {
      title: "ORM Success Stories",
      description:
        "Reputation management initiatives focused on stronger customer trust, review quality, and brand credibility.",
      items: [
        {
          title: "Hospitality Reputation Management",
          description:
            "Improved review management and customer response processes to strengthen guest confidence and online brand perception.",
          image: "/project-image.webp",
        },
        {
          title: "Healthcare Review Strategy",
          description:
            "Established structured review monitoring and engagement practices designed to improve patient trust and reputation visibility.",
          image: "/project-image.webp",
        },
        {
          title: "E-Commerce Brand Monitoring",
          description:
            "Created a reputation monitoring framework for customer feedback, brand mentions, and emerging reputation concerns.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Online Reputation Management Experts",
      description:
        "Partner with our ORM specialists to monitor your brand, manage customer feedback, protect digital credibility, and build a stronger reputation that supports long-term growth.",
      cta: "Let's Strengthen Your Reputation",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to manage customer feedback and strengthen their digital reputation.",
      items: [
        {
          name: "Daniel Wilson",
          company: "UrbanStay",
          location: "United Kingdom",
          review:
            "The team helped us establish a much more organized approach to online reviews and customer feedback. Our response process and overall brand perception improved significantly.",
        },
        {
          name: "Emma Davis",
          company: "CarePoint",
          location: "Australia",
          review:
            "Skand Tech Hub gave us better visibility into our online reputation and helped our team manage customer feedback professionally and consistently.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our Online Reputation Management services.",
      items: [
        {
          question: "What is Online Reputation Management?",
          answer:
            "Online Reputation Management involves monitoring and improving how a brand or business is perceived across search engines, review platforms, social media, and other digital channels.",
        },
        {
          question: "Why does my business need ORM services?",
          answer:
            "Customers frequently research businesses and read reviews before making decisions. ORM helps you monitor that digital perception, respond professionally to feedback, and build stronger customer trust.",
        },
        {
          question: "Can you remove negative reviews?",
          answer:
            "Legitimate reviews generally cannot simply be removed. We focus on professional response management, platform-compliant reporting where appropriate, customer engagement, and building a stronger overall reputation.",
        },
        {
          question: "Which platforms do you monitor?",
          answer:
            "The platforms depend on your business, but ORM can cover Google Business Profile, review websites, social media platforms, search results, and other relevant sources where customers discuss your brand.",
        },
        {
          question: "Do you provide ORM reports?",
          answer:
            "Yes. Reporting can include review trends, customer sentiment, brand mentions, reputation changes, response activity, and recommendations for continued improvement.",
        },
      ],
    },
  },
  // Ppc Services
  {
    title: "PPC Management",
    slug: "ppc-management",
    group: "Digital Marketing Solutions",
    icon: LineChart,

    seo: {
      title: "PPC Management Services",
      description:
        "Generate qualified leads and maximize advertising ROI with professional PPC management services covering Google Ads, paid search, keyword targeting, conversion tracking, and campaign optimization.",
      keywords: [
        "PPC Management",
        "PPC Services",
        "Google Ads Management",
        "Pay Per Click Advertising",
        "Paid Search Marketing",
        "PPC Management Company",
      ],
    },

    hero: {
      title: "PPC Management That Turns Ad Spend Into Growth",
      description:
        "Reach high-intent customers and generate measurable results with strategically managed PPC campaigns focused on conversions, cost efficiency, and sustainable advertising performance.",
      highlights: [
        "Google Ads",
        "Keyword Research",
        "Conversion Tracking",
        "Campaign Optimization",
      ],
    },

    whyChoose: {
      title: "Why Invest in PPC Management?",
      description:
        "PPC advertising helps businesses reach customers at the moment of intent while providing measurable control over targeting, budgets, conversions, and campaign performance.",
      items: [
        {
          title: "Reach High-Intent Customers",
          description:
            "Place your business in front of users actively searching for your products or services.",
          icon: Target,
        },
        {
          title: "Generate Faster Results",
          description:
            "Start driving targeted traffic, enquiries, and conversions without waiting for long-term organic growth.",
          icon: TrendingUp,
        },
        {
          title: "Control Advertising Spend",
          description:
            "Manage budgets, bids, targeting, and campaign priorities based on your business objectives.",
          icon: BarChart3,
        },
        {
          title: "Measure Every Conversion",
          description:
            "Track clicks, calls, leads, purchases, and other valuable actions with accurate conversion measurement.",
          icon: LineChart,
        },
      ],
    },

    services: {
      title: "Our PPC Management Services",
      description:
        "End-to-end paid advertising solutions designed to attract qualified audiences, improve conversions, and maximize return on ad spend.",
      items: [
        {
          title: "Google Search Ads",
          description:
            "Reach customers actively searching for relevant products and services with high-intent search advertising campaigns.",
          icon: Search,
          features: [
            "Search Campaign Setup",
            "Keyword Targeting",
            "Ad Copywriting",
            "Bid Management",
            "Search Term Optimization",
          ],
        },
        {
          title: "Display & Remarketing Ads",
          description:
            "Reconnect with potential customers and expand brand visibility through targeted display and remarketing campaigns.",
          icon: Megaphone,
          features: [
            "Display Campaigns",
            "Remarketing Audiences",
            "Banner Advertising",
            "Audience Targeting",
            "Frequency Optimization",
          ],
        },
        {
          title: "Shopping Campaigns",
          description:
            "Promote e-commerce products with optimized shopping campaigns designed to increase qualified traffic and online sales.",
          icon: Target,
          features: [
            "Product Feed Optimization",
            "Shopping Campaign Setup",
            "Product Segmentation",
            "Bid Optimization",
            "Conversion Tracking",
          ],
        },
        {
          title: "PPC Campaign Optimization",
          description:
            "Continuously improve campaign efficiency through testing, budget management, conversion analysis, and performance optimization.",
          icon: LineChart,
          features: [
            "A/B Testing",
            "Budget Optimization",
            "Negative Keywords",
            "Conversion Analysis",
            "Performance Reporting",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Everything required to build, measure, and continuously improve performance-focused PPC campaigns.",
      items: [
        {
          title: "Keyword Research",
          description:
            "Identify commercially relevant keywords based on search intent, competition, and conversion potential.",
          icon: Search,
        },
        {
          title: "Audience Targeting",
          description:
            "Reach relevant prospects using location, demographic, interest, intent, and remarketing audiences.",
          icon: Target,
        },
        {
          title: "Conversion Tracking",
          description:
            "Measure leads, purchases, calls, forms, and other valuable actions generated through advertising.",
          icon: BarChart3,
        },
        {
          title: "Campaign Analytics",
          description:
            "Monitor costs, conversions, search terms, ROI, and campaign performance through detailed reporting.",
          icon: LineChart,
        },
      ],
    },

    process: {
      title: "Our PPC Management Process",
      description:
        "A structured advertising process focused on targeting high-intent audiences, controlling costs, and maximizing conversions.",
      items: [
        {
          title: "Business & Campaign Analysis",
          description:
            "Understand your business goals, customers, competitors, advertising history, budget, and conversion objectives.",
        },
        {
          title: "Keyword & Audience Research",
          description:
            "Identify valuable search terms, audience segments, targeting opportunities, and negative keywords.",
        },
        {
          title: "Campaign Strategy & Setup",
          description:
            "Build structured campaigns with relevant ad groups, targeting, budgets, bidding strategies, and conversion tracking.",
        },
        {
          title: "Ad & Landing Page Alignment",
          description:
            "Create relevant ad messaging and align campaigns with landing pages designed around user intent and conversions.",
        },
        {
          title: "Testing & Optimization",
          description:
            "Continuously test keywords, advertisements, bids, audiences, and budgets to improve campaign efficiency.",
        },
        {
          title: "Reporting & Scaling",
          description:
            "Analyze campaign performance, identify profitable opportunities, and scale successful campaigns based on measurable results.",
        },
      ],
    },

    benefits: {
      title: "Benefits of PPC Management",
      description:
        "Generate measurable customer acquisition while maintaining greater control over advertising budgets and performance.",
      items: [
        {
          title: "Immediate Search Visibility",
          description:
            "Appear prominently for relevant searches and start reaching potential customers quickly.",
          icon: TrendingUp,
        },
        {
          title: "Highly Targeted Traffic",
          description:
            "Attract users based on keywords, intent, location, demographics, audiences, and other targeting signals.",
          icon: Target,
        },
        {
          title: "Measurable ROI",
          description:
            "Connect advertising spend with leads, sales, and conversions to understand campaign profitability.",
          icon: BarChart3,
        },
        {
          title: "Flexible Budget Control",
          description:
            "Adjust advertising budgets and campaign priorities based on performance and business requirements.",
          icon: LineChart,
        },
        {
          title: "Remarketing Opportunities",
          description:
            "Reconnect with previous website visitors and potential customers who have already shown interest.",
          icon: Megaphone,
        },
        {
          title: "Actionable Customer Insights",
          description:
            "Use keyword, audience, and conversion data to better understand customer demand and buying behaviour.",
          icon: Search,
        },
      ],
    },

    techStack: {
      title: "PPC Platforms & Tools",
      description:
        "Advertising and analytics platforms used to manage campaigns, measure conversions, and optimize paid acquisition.",
      technologies: [
        "Google Ads",
        "Google Keyword Planner",
        "Google Analytics",
        "Google Tag Manager",
        "Looker Studio",
        "Google Merchant Center",
        "Meta Ads",
        "Microsoft Advertising",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine paid search strategy, conversion tracking, audience insights, and continuous optimization to make advertising spend more accountable.",
      items: [
        {
          title: "Conversion-Focused Strategy",
          description:
            "Build campaigns around meaningful business actions rather than focusing only on clicks and impressions.",
          icon: Target,
        },
        {
          title: "Data-Driven Optimization",
          description:
            "Use search terms, conversion data, costs, and audience insights to continuously improve campaign performance.",
          icon: BarChart3,
        },
        {
          title: "Strategic Budget Management",
          description:
            "Allocate advertising budgets toward campaigns, keywords, and audiences showing stronger performance.",
          icon: LineChart,
        },
        {
          title: "Accurate Tracking",
          description:
            "Implement conversion measurement so campaign decisions are supported by meaningful performance data.",
          icon: Search,
        },
        {
          title: "Transparent Reporting",
          description:
            "Receive clear reports covering spend, clicks, conversions, costs, and opportunities for improvement.",
          icon: ShieldCheck,
        },
        {
          title: "Continuous Campaign Improvement",
          description:
            "Regularly test and refine campaigns to reduce wasted spend and identify scalable opportunities.",
          icon: TrendingUp,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "PPC strategies tailored to different customer journeys, conversion goals, competition levels, and business models.",
      items: [
        {
          title: "E-Commerce",
          description:
            "Increase product visibility and online sales through search, shopping, display, and remarketing campaigns.",
          icon: TrendingUp,
        },
        {
          title: "Real Estate",
          description:
            "Generate qualified property enquiries with location and intent-focused advertising campaigns.",
          icon: Target,
        },
        {
          title: "Healthcare",
          description:
            "Reach prospective patients through carefully targeted search campaigns and conversion-focused landing journeys.",
          icon: ShieldCheck,
        },
        {
          title: "Education",
          description:
            "Generate student enquiries and admissions leads through targeted search and display advertising.",
          icon: Search,
        },
        {
          title: "Professional Services",
          description:
            "Connect service businesses with customers actively searching for relevant expertise and solutions.",
          icon: Globe,
        },
        {
          title: "Technology & SaaS",
          description:
            "Generate qualified product enquiries, demo requests, and B2B opportunities through targeted paid acquisition.",
          icon: BarChart3,
        },
      ],
    },

    portfolio: {
      title: "PPC Success Stories",
      description:
        "Paid advertising campaigns focused on qualified traffic, lead generation, conversion efficiency, and measurable growth.",
      items: [
        {
          title: "E-Commerce Shopping Campaign",
          description:
            "Optimized product targeting, campaign structure, and conversion tracking to improve paid shopping performance.",
          image: "/project-image.webp",
        },
        {
          title: "Real Estate Lead Generation",
          description:
            "Built high-intent search campaigns focused on location-specific property searches and qualified enquiries.",
          image: "/project-image.webp",
        },
        {
          title: "B2B Search Advertising",
          description:
            "Structured keyword and conversion-focused campaigns designed to generate relevant business enquiries.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire PPC Management Experts",
      description:
        "Partner with our PPC specialists to build targeted advertising campaigns, improve conversion tracking, control acquisition costs, and maximize the value of your advertising budget.",
      cta: "Let's Optimize Your Ad Spend",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to manage paid campaigns with greater visibility, control, and performance focus.",
      items: [
        {
          name: "Ryan Cooper",
          company: "CommerceEdge",
          location: "United States",
          review:
            "The team brought much better structure to our paid campaigns and helped us understand where our advertising budget was actually producing results.",
        },
        {
          name: "Olivia Martin",
          company: "UrbanProperty",
          location: "United Kingdom",
          review:
            "Our campaigns became significantly more focused after Skand Tech Hub took over the account. The reporting and ongoing optimization have been especially valuable.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our PPC Management services.",
      items: [
        {
          question: "What is PPC advertising?",
          answer:
            "PPC, or pay-per-click advertising, is a digital advertising model where businesses can place ads across platforms such as Google and typically pay when users interact with those advertisements.",
        },
        {
          question: "Which PPC platforms do you manage?",
          answer:
            "Depending on your requirements, we can manage platforms such as Google Ads, Microsoft Advertising, Google Shopping, display advertising, remarketing, and complementary paid acquisition channels.",
        },
        {
          question: "How much should I spend on PPC?",
          answer:
            "The appropriate budget depends on your industry, competition, keyword costs, location, conversion value, and growth objectives. We build campaign recommendations around your specific business economics.",
        },
        {
          question: "How quickly can PPC generate results?",
          answer:
            "PPC campaigns can begin generating traffic shortly after launch, but achieving efficient and consistent performance usually requires ongoing testing, conversion data, and campaign optimization.",
        },
        {
          question: "Do you provide conversion tracking and PPC reports?",
          answer:
            "Yes. We can implement conversion tracking and provide reports covering advertising spend, clicks, conversions, cost per acquisition, search performance, and optimization opportunities.",
        },
      ],
    },
  },
  // Video Editing Services
  {
    title: "Video Editing",
    slug: "video-editing",
    group: "Digital Marketing Solutions",
    icon: Video,

    seo: {
      title: "Professional Video Editing Services",
      description:
        "Create engaging, polished, and platform-ready videos with professional video editing services for brands, social media, marketing campaigns, products, and corporate communication.",
      keywords: [
        "Video Editing Services",
        "Professional Video Editing",
        "Social Media Video Editing",
        "Corporate Video Editing",
        "YouTube Video Editing",
        "Video Editing Company",
      ],
    },

    hero: {
      title: "Video Editing That Turns Raw Footage Into Powerful Stories",
      description:
        "Transform raw footage into polished, engaging, and brand-focused videos with professional editing, motion graphics, sound design, and platform-specific creative execution.",
      highlights: [
        "Professional Video Editing",
        "Social Media Videos",
        "Motion Graphics",
        "Brand & Marketing Videos",
      ],
    },

    whyChoose: {
      title: "Why Invest in Professional Video Editing?",
      description:
        "Professional editing transforms raw footage into compelling visual communication that captures attention, strengthens your brand, and improves content quality.",
      items: [
        {
          title: "Capture Audience Attention",
          description:
            "Create engaging videos structured to capture interest and keep viewers watching.",
          icon: Video,
        },
        {
          title: "Strengthen Brand Identity",
          description:
            "Maintain consistent visual language, typography, transitions, and creative direction across video content.",
          icon: Palette,
        },
        {
          title: "Improve Content Quality",
          description:
            "Turn unpolished footage into professional videos with clean edits, audio enhancement, graphics, and visual refinement.",
          icon: TrendingUp,
        },
        {
          title: "Optimize for Every Platform",
          description:
            "Adapt videos for different formats, durations, aspect ratios, and audience behaviours across digital platforms.",
          icon: Target,
        },
      ],
    },

    services: {
      title: "Our Video Editing Services",
      description:
        "End-to-end video post-production solutions designed for brands, businesses, creators, and digital marketing campaigns.",
      items: [
        {
          title: "Social Media Video Editing",
          description:
            "Create fast-paced and engaging videos optimized for social media audiences and platform-specific formats.",
          icon: Video,
          features: [
            "Instagram Reels",
            "YouTube Shorts",
            "Social Media Ads",
            "Vertical Videos",
            "Content Cutdowns",
          ],
        },
        {
          title: "Corporate Video Editing",
          description:
            "Produce polished corporate videos that communicate your business, products, services, and brand professionally.",
          icon: FileText,
          features: [
            "Company Profiles",
            "Corporate Presentations",
            "Interview Editing",
            "Training Videos",
            "Internal Communication",
          ],
        },
        {
          title: "Motion Graphics & Animation",
          description:
            "Enhance video content with animated graphics, titles, visual effects, and branded motion elements.",
          icon: Palette,
          features: [
            "Motion Graphics",
            "Animated Titles",
            "Logo Animation",
            "Lower Thirds",
            "Visual Effects",
          ],
        },
        {
          title: "Marketing & Promotional Videos",
          description:
            "Edit conversion-focused videos designed for advertising, product promotion, campaigns, and digital marketing.",
          icon: Megaphone,
          features: [
            "Product Videos",
            "Promotional Videos",
            "Video Advertisements",
            "Campaign Creatives",
            "Brand Videos",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Everything required to transform raw footage into professional, engaging, and platform-ready video content.",
      items: [
        {
          title: "Professional Editing",
          description:
            "Structure footage with clean cuts, pacing, transitions, and storytelling that keeps viewers engaged.",
          icon: Video,
        },
        {
          title: "Motion Graphics",
          description:
            "Add professional titles, animations, graphics, overlays, and branded visual elements.",
          icon: Palette,
        },
        {
          title: "Audio Enhancement",
          description:
            "Improve dialogue clarity, balance audio levels, add music, and enhance the overall sound experience.",
          icon: TrendingUp,
        },
        {
          title: "Multi-Platform Formatting",
          description:
            "Prepare videos in appropriate dimensions, durations, and formats for different digital platforms.",
          icon: Target,
        },
      ],
    },

    process: {
      title: "Our Video Editing Process",
      description:
        "A structured post-production workflow focused on storytelling, brand consistency, visual quality, and platform readiness.",
      items: [
        {
          title: "Content & Requirement Analysis",
          description:
            "Understand the video objective, audience, platform, brand requirements, raw footage, and creative direction.",
        },
        {
          title: "Footage Organization",
          description:
            "Review and organize raw footage, audio, graphics, brand assets, and other production materials.",
        },
        {
          title: "Rough Cut & Story Structure",
          description:
            "Build the initial sequence, establish pacing, select strong footage, and create the core narrative structure.",
        },
        {
          title: "Creative Enhancement",
          description:
            "Add transitions, titles, motion graphics, music, sound effects, branding, and other visual enhancements.",
        },
        {
          title: "Review & Refinement",
          description:
            "Refine timing, visuals, audio, graphics, and overall presentation based on review and feedback.",
        },
        {
          title: "Final Export & Delivery",
          description:
            "Export optimized versions in the required resolutions, aspect ratios, and formats for selected platforms.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Professional Video Editing",
      description:
        "Create higher-quality visual content that strengthens communication, engagement, and overall brand presentation.",
      items: [
        {
          title: "Higher Audience Engagement",
          description:
            "Well-paced and professionally edited videos are more effective at capturing and maintaining viewer attention.",
          icon: TrendingUp,
        },
        {
          title: "Professional Brand Image",
          description:
            "Present your business with polished visual content that reinforces credibility and brand quality.",
          icon: ShieldCheck,
        },
        {
          title: "Stronger Storytelling",
          description:
            "Transform disconnected footage into clear narratives that communicate your message more effectively.",
          icon: Video,
        },
        {
          title: "Platform-Ready Content",
          description:
            "Prepare videos specifically for social media, advertising, websites, presentations, and other digital channels.",
          icon: Globe,
        },
        {
          title: "Better Marketing Creatives",
          description:
            "Create compelling video assets that support awareness, engagement, product promotion, and campaign performance.",
          icon: Megaphone,
        },
        {
          title: "Consistent Visual Identity",
          description:
            "Maintain recognizable typography, graphics, colours, motion, and brand presentation across video content.",
          icon: Palette,
        },
      ],
    },

    techStack: {
      title: "Video Editing Tools & Technologies",
      description:
        "Professional editing, motion graphics, colour, audio, and creative tools used throughout our post-production workflow.",
      technologies: [
        "Adobe Premiere Pro",
        "Adobe After Effects",
        "DaVinci Resolve",
        "Adobe Audition",
        "Adobe Photoshop",
        "Adobe Illustrator",
        "Figma",
        "Motion Graphics",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine creative storytelling, technical editing, branding, and digital marketing understanding to produce videos built around real communication goals.",
      items: [
        {
          title: "Creative Storytelling",
          description:
            "Structure footage around clear narratives that make content more engaging and easier to understand.",
          icon: Video,
        },
        {
          title: "Brand-Focused Editing",
          description:
            "Maintain visual consistency with your brand identity, messaging, and overall creative direction.",
          icon: Palette,
        },
        {
          title: "Platform Expertise",
          description:
            "Edit content according to the format, pacing, dimensions, and audience behaviour of different platforms.",
          icon: Globe,
        },
        {
          title: "Marketing Perspective",
          description:
            "Create video assets that support broader content, advertising, product, and brand objectives.",
          icon: Target,
        },
        {
          title: "Quality-Focused Execution",
          description:
            "Pay attention to pacing, audio, transitions, graphics, colour, and other details throughout post-production.",
          icon: ShieldCheck,
        },
        {
          title: "Flexible Video Formats",
          description:
            "Create long-form videos, short-form cutdowns, advertisements, reels, presentations, and other content from available assets.",
          icon: TrendingUp,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "Professional video editing solutions tailored to different audiences, communication objectives, and digital content requirements.",
      items: [
        {
          title: "E-Commerce & Retail",
          description:
            "Create product videos, advertisements, social content, and promotional assets designed to showcase products effectively.",
          icon: TrendingUp,
        },
        {
          title: "Real Estate",
          description:
            "Produce polished property walkthroughs, project videos, social reels, and promotional content.",
          icon: Globe,
        },
        {
          title: "Education",
          description:
            "Edit educational videos, online courses, institutional content, tutorials, and promotional campaigns.",
          icon: FileText,
        },
        {
          title: "Healthcare",
          description:
            "Create professional informational, educational, testimonial, and brand communication videos.",
          icon: ShieldCheck,
        },
        {
          title: "Hospitality & Travel",
          description:
            "Transform destination, property, food, and experience footage into visually engaging promotional content.",
          icon: Video,
        },
        {
          title: "Technology & SaaS",
          description:
            "Create product demonstrations, explainers, launch videos, tutorials, and digital marketing creatives.",
          icon: Target,
        },
      ],
    },

    portfolio: {
      title: "Video Editing Success Stories",
      description:
        "Post-production projects focused on stronger storytelling, professional presentation, and platform-ready visual content.",
      items: [
        {
          title: "Social Media Content Series",
          description:
            "Converted long-form footage into engaging short-form videos optimized for reels, shorts, and social campaigns.",
          image: "/project-image.webp",
        },
        {
          title: "Product Promotional Video",
          description:
            "Combined product footage, motion graphics, typography, music, and branded visuals into a polished promotional asset.",
          image: "/project-image.webp",
        },
        {
          title: "Corporate Brand Video",
          description:
            "Structured interviews, business footage, graphics, and brand elements into a professional corporate presentation.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Professional Video Editors",
      description:
        "Partner with our video editing team to transform raw footage into polished brand videos, social content, promotional creatives, and professional visual assets tailored to your audience and platforms.",
      cta: "Let's Create Your Next Video",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to transform their footage into polished, engaging, and professionally presented video content.",
      items: [
        {
          name: "Ethan Miller",
          company: "VisionCraft",
          location: "United States",
          review:
            "The team understood the style we were looking for and turned our raw footage into polished social content that felt much more professional and consistent.",
        },
        {
          name: "Charlotte Evans",
          company: "BrandSphere",
          location: "United Kingdom",
          review:
            "Skand Tech Hub handled the editing, graphics, and overall pacing extremely well. The final videos aligned closely with our brand and campaign requirements.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our professional Video Editing services.",
      items: [
        {
          question: "What types of videos do you edit?",
          answer:
            "We can edit social media videos, reels, YouTube content, corporate videos, product videos, advertisements, interviews, promotional videos, educational content, and other digital video formats.",
        },
        {
          question: "Can you create short videos from long-form footage?",
          answer:
            "Yes. We can repurpose long-form footage into shorter clips, reels, shorts, advertisements, teasers, and other platform-specific content.",
        },
        {
          question: "Do you provide motion graphics and animation?",
          answer:
            "Yes. Depending on the project, we can incorporate animated titles, lower thirds, logo animations, graphics, transitions, visual effects, and other motion elements.",
        },
        {
          question: "Can you edit videos for different social media platforms?",
          answer:
            "Yes. We can prepare platform-specific versions with appropriate aspect ratios, durations, pacing, captions, and formats for channels such as Instagram, Facebook, YouTube, LinkedIn, and others.",
        },
        {
          question: "What do you need to start a video editing project?",
          answer:
            "Typically we need your raw footage, brand assets, project objective, preferred style or references, required platforms, and any specific messaging, music, graphics, or delivery requirements.",
        },
      ],
    },
  },
  // Brand Development Services
  {
    title: "Brand Development",
    slug: "brand-development",
    group: "Digital Marketing Solutions",
    icon: Paintbrush,

    seo: {
      title: "Brand Development Services",
      description:
        "Build a distinctive and consistent brand with professional brand development services covering strategy, positioning, messaging, visual identity, and digital brand guidelines.",
      keywords: [
        "Brand Development",
        "Brand Development Services",
        "Brand Strategy",
        "Brand Identity Design",
        "Brand Positioning",
        "Brand Development Company",
      ],
    },

    hero: {
      title: "Brand Development That Builds Recognition and Trust",
      description:
        "Create a clear, memorable, and consistent brand with strategic positioning, compelling messaging, visual identity systems, and guidelines designed to strengthen your presence across every customer touchpoint.",
      highlights: [
        "Brand Strategy",
        "Brand Positioning",
        "Visual Identity",
        "Brand Guidelines",
      ],
    },

    whyChoose: {
      title: "Why Invest in Brand Development?",
      description:
        "A strong brand creates recognition, communicates value, builds customer trust, and gives your business a consistent identity across marketing, sales, products, and digital channels.",
      items: [
        {
          title: "Build Brand Recognition",
          description:
            "Create a distinctive identity that helps customers recognize and remember your business.",
          icon: TrendingUp,
        },
        {
          title: "Strengthen Customer Trust",
          description:
            "Present your business consistently and professionally across every customer interaction.",
          icon: ShieldCheck,
        },
        {
          title: "Differentiate Your Business",
          description:
            "Define clear positioning that communicates why customers should choose your brand over competitors.",
          icon: Target,
        },
        {
          title: "Create Brand Consistency",
          description:
            "Establish clear visual and messaging standards that keep your brand aligned across channels.",
          icon: Palette,
        },
      ],
    },

    services: {
      title: "Our Brand Development Services",
      description:
        "Comprehensive brand development solutions designed to define how your business looks, communicates, and positions itself in the market.",
      items: [
        {
          title: "Brand Strategy & Positioning",
          description:
            "Define your brand's market position, audience, value proposition, personality, and competitive differentiation.",
          icon: Target,
          features: [
            "Brand Discovery",
            "Audience Definition",
            "Competitive Analysis",
            "Brand Positioning",
            "Value Proposition",
          ],
        },
        {
          title: "Brand Messaging",
          description:
            "Develop a clear communication framework that expresses your brand consistently across marketing and customer touchpoints.",
          icon: MessageCircle,
          features: [
            "Brand Voice",
            "Core Messaging",
            "Tagline Development",
            "Value Messaging",
            "Communication Framework",
          ],
        },
        {
          title: "Visual Identity Development",
          description:
            "Create a cohesive visual system that gives your brand a recognizable and professional appearance.",
          icon: Palette,
          features: [
            "Logo Direction",
            "Color System",
            "Typography",
            "Visual Elements",
            "Brand Applications",
          ],
        },
        {
          title: "Brand Guidelines",
          description:
            "Document visual and communication standards so your brand remains consistent as your business grows.",
          icon: FileText,
          features: [
            "Logo Guidelines",
            "Color Guidelines",
            "Typography Rules",
            "Brand Voice",
            "Usage Standards",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Everything required to create a clear, recognizable, and scalable brand system.",
      items: [
        {
          title: "Brand Research",
          description:
            "Analyze your market, audience, competitors, and existing brand perception before defining the strategy.",
          icon: Search,
        },
        {
          title: "Strategic Positioning",
          description:
            "Establish how your brand should be positioned and differentiated within its target market.",
          icon: Target,
        },
        {
          title: "Visual Identity System",
          description:
            "Build consistent visual elements that make your brand recognizable across digital and physical touchpoints.",
          icon: Palette,
        },
        {
          title: "Brand Documentation",
          description:
            "Create practical guidelines that help internal teams and partners maintain brand consistency.",
          icon: FileText,
        },
      ],
    },

    process: {
      title: "Our Brand Development Process",
      description:
        "A structured process that transforms business objectives and audience insights into a clear and consistent brand system.",
      items: [
        {
          title: "Brand Discovery",
          description:
            "Understand your business, vision, values, products, customers, market position, and long-term objectives.",
        },
        {
          title: "Market & Audience Research",
          description:
            "Analyze competitors, customer expectations, industry positioning, and opportunities for differentiation.",
        },
        {
          title: "Brand Strategy",
          description:
            "Define positioning, personality, value proposition, audience, messaging direction, and strategic brand foundations.",
        },
        {
          title: "Identity Development",
          description:
            "Develop the visual and communication elements required to express the brand consistently.",
        },
        {
          title: "Brand System Refinement",
          description:
            "Review and refine messaging, visual elements, applications, and overall brand consistency.",
        },
        {
          title: "Guidelines & Implementation",
          description:
            "Document the final brand system and prepare assets for consistent implementation across relevant channels.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Professional Brand Development",
      description:
        "Create a stronger business identity that improves recognition, differentiation, communication, and customer confidence.",
      items: [
        {
          title: "Stronger Brand Recognition",
          description:
            "Build a distinctive identity that customers can recognize across different platforms and interactions.",
          icon: TrendingUp,
        },
        {
          title: "Clear Market Positioning",
          description:
            "Communicate what makes your business different and why your offering matters to your target audience.",
          icon: Target,
        },
        {
          title: "Consistent Brand Experience",
          description:
            "Maintain alignment across your website, marketing, social media, sales materials, and other touchpoints.",
          icon: Palette,
        },
        {
          title: "Greater Customer Trust",
          description:
            "Present a professional and cohesive identity that strengthens confidence in your business.",
          icon: ShieldCheck,
        },
        {
          title: "Better Marketing Communication",
          description:
            "Give marketing and sales teams a clear messaging framework for communicating the brand effectively.",
          icon: MessageCircle,
        },
        {
          title: "Scalable Brand Foundation",
          description:
            "Create guidelines and systems that make it easier to maintain consistency as your organization grows.",
          icon: BarChart3,
        },
      ],
    },

    techStack: {
      title: "Brand Development Tools",
      description:
        "Creative, design, research, and collaboration tools used to develop and document modern brand systems.",
      technologies: [
        "Figma",
        "Adobe Illustrator",
        "Adobe Photoshop",
        "Adobe InDesign",
        "Brand Systems",
        "Content Strategy",
        "Typography",
        "Visual Identity",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine business strategy, creative thinking, visual design, and digital experience to build brands that are practical, consistent, and ready to grow.",
      items: [
        {
          title: "Strategy-Driven Branding",
          description:
            "Build the brand around your business objectives, audience, positioning, and competitive environment.",
          icon: Target,
        },
        {
          title: "Creative Brand Identity",
          description:
            "Develop visual systems that are distinctive, professional, and aligned with your brand personality.",
          icon: Palette,
        },
        {
          title: "Audience-Focused Messaging",
          description:
            "Create communication that clearly expresses value and connects with the customers you want to reach.",
          icon: MessageCircle,
        },
        {
          title: "Digital-First Perspective",
          description:
            "Design brand systems with websites, applications, social media, campaigns, and modern digital experiences in mind.",
          icon: Globe,
        },
        {
          title: "Consistent Brand Systems",
          description:
            "Establish practical standards that help teams maintain consistency across different channels and materials.",
          icon: ShieldCheck,
        },
        {
          title: "Growth-Ready Foundation",
          description:
            "Create a scalable identity and communication system capable of evolving alongside your business.",
          icon: TrendingUp,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "Brand development solutions tailored to different markets, audiences, competitive environments, and customer expectations.",
      items: [
        {
          title: "Technology & SaaS",
          description:
            "Create modern technology brands that communicate innovation, value, and product differentiation clearly.",
          icon: BarChart3,
        },
        {
          title: "E-Commerce & Retail",
          description:
            "Develop memorable consumer brands with consistent identities across products, stores, marketing, and digital commerce.",
          icon: TrendingUp,
        },
        {
          title: "Real Estate",
          description:
            "Build credible property and development brands designed to connect with buyers, investors, and partners.",
          icon: Globe,
        },
        {
          title: "Healthcare",
          description:
            "Create professional healthcare brands focused on clarity, credibility, trust, and patient communication.",
          icon: ShieldCheck,
        },
        {
          title: "Education",
          description:
            "Develop distinctive institutional and EdTech brands that communicate value to students and parents.",
          icon: FileText,
        },
        {
          title: "Hospitality & Travel",
          description:
            "Build experience-focused brands with memorable visual identities and compelling customer communication.",
          icon: Palette,
        },
      ],
    },

    portfolio: {
      title: "Brand Development Success Stories",
      description:
        "Brand development projects focused on stronger positioning, visual consistency, and clearer customer communication.",
      items: [
        {
          title: "Technology Brand Identity",
          description:
            "Developed strategic positioning, messaging direction, and a cohesive visual identity for a growing technology business.",
          image: "/project-image.webp",
        },
        {
          title: "E-Commerce Brand Development",
          description:
            "Created a consistent consumer brand system covering visual identity, messaging, digital presentation, and marketing applications.",
          image: "/project-image.webp",
        },
        {
          title: "Corporate Brand Refresh",
          description:
            "Refined positioning, visual language, and communication standards to create a more modern and consistent corporate presence.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Brand Development Experts",
      description:
        "Partner with our brand development team to define your positioning, messaging, visual identity, and brand guidelines while creating a consistent foundation for long-term business growth.",
      cta: "Let's Build Your Brand",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to turn their ideas, positioning, and values into clear and consistent brand systems.",
      items: [
        {
          name: "Noah Anderson",
          company: "NovaWorks",
          location: "United States",
          review:
            "The team helped us bring much more clarity to our positioning and visual identity. Our brand now feels consistent across our website, presentations, and marketing materials.",
        },
        {
          name: "Amelia Clark",
          company: "UrbanNest",
          location: "United Kingdom",
          review:
            "Skand Tech Hub understood the direction we wanted and translated it into a professional brand system that our entire team can use consistently.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our Brand Development services.",
      items: [
        {
          question: "What is included in brand development?",
          answer:
            "Brand development can include research, strategy, positioning, audience definition, messaging, visual identity, brand voice, and guidelines depending on your business requirements.",
        },
        {
          question: "What is the difference between branding and logo design?",
          answer:
            "A logo is one visual element of a brand. Brand development is broader and defines your positioning, messaging, personality, visual identity, communication standards, and how the business is presented across customer touchpoints.",
        },
        {
          question: "Can you refresh an existing brand?",
          answer:
            "Yes. We can evaluate your existing brand and refine its positioning, messaging, visual identity, or guidelines while retaining elements that still support your business.",
        },
        {
          question: "Do you create brand guidelines?",
          answer:
            "Yes. Brand guidelines can document logo usage, colours, typography, visual elements, brand voice, messaging, and other standards required to maintain consistency.",
        },
        {
          question: "Can you help startups build a brand from scratch?",
          answer:
            "Yes. We can work from the early stages to define your target audience, positioning, value proposition, messaging, visual identity, and practical brand system for launch.",
        },
      ],
    },
  },
  // Email Marketing Services
  {
    title: "Email Marketing",
    slug: "email-marketing",
    group: "Digital Marketing Solutions",
    icon: Bot,

    seo: {
      title: "Email Marketing Services",
      description:
        "Drive engagement, conversions, and customer retention with professional email marketing services covering campaigns, automation, segmentation, personalization, and performance optimization.",
      keywords: [
        "Email Marketing Services",
        "Email Marketing",
        "Email Automation",
        "Email Campaign Management",
        "Email Marketing Strategy",
        "Email Marketing Company",
      ],
    },

    hero: {
      title: "Email Marketing That Turns Subscribers Into Customers",
      description:
        "Build meaningful customer relationships and generate measurable revenue with targeted email campaigns, automated journeys, personalized communication, and data-driven optimization.",
      highlights: [
        "Email Campaigns",
        "Marketing Automation",
        "Audience Segmentation",
        "Performance Analytics",
      ],
    },

    whyChoose: {
      title: "Why Invest in Email Marketing?",
      description:
        "Email marketing gives businesses a direct channel to nurture prospects, engage customers, promote offers, and strengthen long-term customer relationships.",
      items: [
        {
          title: "Nurture Qualified Leads",
          description:
            "Guide prospects through personalized email journeys based on their interests, actions, and buying stage.",
          icon: Target,
        },
        {
          title: "Increase Customer Retention",
          description:
            "Stay connected with existing customers through valuable, timely, and relevant communication.",
          icon: MessageCircle,
        },
        {
          title: "Automate Customer Journeys",
          description:
            "Deliver the right message automatically based on customer behaviour, triggers, and lifecycle events.",
          icon: Bot,
        },
        {
          title: "Measure Campaign Performance",
          description:
            "Track engagement, clicks, conversions, and campaign results to continuously improve performance.",
          icon: BarChart3,
        },
      ],
    },

    services: {
      title: "Our Email Marketing Services",
      description:
        "End-to-end email marketing solutions designed to improve engagement, nurture leads, increase conversions, and strengthen customer retention.",
      items: [
        {
          title: "Email Campaign Management",
          description:
            "Plan, create, execute, and optimize targeted email campaigns aligned with your marketing objectives.",
          icon: MessageCircle,
          features: [
            "Campaign Strategy",
            "Email Copywriting",
            "Campaign Scheduling",
            "Audience Targeting",
            "Performance Tracking",
          ],
        },
        {
          title: "Email Automation",
          description:
            "Create automated email journeys that respond intelligently to customer actions and lifecycle stages.",
          icon: Bot,
          features: [
            "Welcome Sequences",
            "Lead Nurturing",
            "Abandoned Cart Emails",
            "Re-Engagement Campaigns",
            "Lifecycle Automation",
          ],
        },
        {
          title: "Audience Segmentation",
          description:
            "Organize subscribers into meaningful segments for more relevant and personalized communication.",
          icon: Target,
          features: [
            "Customer Segmentation",
            "Behavioural Segmentation",
            "Interest-Based Groups",
            "Lifecycle Segments",
            "Personalized Messaging",
          ],
        },
        {
          title: "Campaign Optimization",
          description:
            "Improve email performance through testing, analytics, deliverability monitoring, and continuous optimization.",
          icon: LineChart,
          features: [
            "A/B Testing",
            "Subject Line Testing",
            "CTA Optimization",
            "Deliverability Monitoring",
            "Conversion Analysis",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Everything required to build targeted, automated, measurable, and conversion-focused email marketing campaigns.",
      items: [
        {
          title: "Personalized Campaigns",
          description:
            "Deliver relevant messages based on subscriber interests, behaviour, preferences, and lifecycle stage.",
          icon: MessageCircle,
        },
        {
          title: "Marketing Automation",
          description:
            "Create automated workflows that communicate with customers at important moments in their journey.",
          icon: Bot,
        },
        {
          title: "Audience Segmentation",
          description:
            "Target specific subscriber groups instead of sending identical messages to your entire audience.",
          icon: Target,
        },
        {
          title: "Campaign Analytics",
          description:
            "Track engagement, clicks, conversions, audience behaviour, and campaign performance.",
          icon: LineChart,
        },
      ],
    },

    process: {
      title: "Our Email Marketing Process",
      description:
        "A structured email marketing process focused on audience relevance, automation, engagement, and measurable business outcomes.",
      items: [
        {
          title: "Business & Audience Discovery",
          description:
            "Understand your business objectives, customer lifecycle, existing subscriber base, offers, and email marketing requirements.",
        },
        {
          title: "Email Strategy",
          description:
            "Define campaign objectives, audience segments, messaging, automation opportunities, frequency, and performance metrics.",
        },
        {
          title: "Campaign & Journey Creation",
          description:
            "Develop email copy, layouts, calls-to-action, campaigns, and automated customer journeys.",
        },
        {
          title: "Segmentation & Setup",
          description:
            "Configure subscriber segments, triggers, workflows, personalization, tracking, and campaign settings.",
        },
        {
          title: "Testing & Launch",
          description:
            "Test content, links, layouts, subject lines, tracking, and deliverability before launching campaigns.",
        },
        {
          title: "Analysis & Optimization",
          description:
            "Measure engagement and conversions while continuously refining campaigns, segments, and automated journeys.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Email Marketing",
      description:
        "Create a direct and measurable communication channel that supports acquisition, conversion, retention, and long-term customer value.",
      items: [
        {
          title: "Direct Customer Communication",
          description:
            "Reach prospects and customers directly with relevant messages without depending entirely on third-party platforms.",
          icon: MessageCircle,
        },
        {
          title: "Higher Customer Retention",
          description:
            "Maintain ongoing relationships with customers through useful content, offers, updates, and personalized communication.",
          icon: ShieldCheck,
        },
        {
          title: "Automated Lead Nurturing",
          description:
            "Move prospects through the customer journey with automated sequences based on behaviour and intent.",
          icon: Bot,
        },
        {
          title: "Personalized Experiences",
          description:
            "Deliver more relevant communication using customer data, interests, actions, and segmentation.",
          icon: Target,
        },
        {
          title: "Measurable Performance",
          description:
            "Track campaign engagement and conversion activity to understand what drives stronger results.",
          icon: BarChart3,
        },
        {
          title: "Scalable Customer Engagement",
          description:
            "Communicate efficiently with growing audiences through reusable campaigns and automated workflows.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "Email Marketing Platforms & Tools",
      description:
        "Email marketing, automation, CRM, analytics, and customer engagement platforms used to build and optimize campaigns.",
      technologies: [
        "Mailchimp",
        "HubSpot",
        "Klaviyo",
        "Brevo",
        "ActiveCampaign",
        "CRM",
        "Google Analytics",
        "Google Tag Manager",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine customer journey strategy, automation, content, segmentation, and analytics to turn email into a measurable growth and retention channel.",
      items: [
        {
          title: "Strategy-Driven Campaigns",
          description:
            "Build email campaigns around customer journeys and business objectives instead of sending disconnected messages.",
          icon: Target,
        },
        {
          title: "Automation Expertise",
          description:
            "Create intelligent workflows that respond to customer actions and important lifecycle events.",
          icon: Bot,
        },
        {
          title: "Audience-Focused Communication",
          description:
            "Use segmentation and personalization to make email communication more relevant to each audience.",
          icon: MessageCircle,
        },
        {
          title: "Conversion-Focused Execution",
          description:
            "Structure messaging, offers, and calls-to-action around meaningful customer actions and business outcomes.",
          icon: TrendingUp,
        },
        {
          title: "Data-Driven Optimization",
          description:
            "Use campaign performance and customer behaviour data to continuously improve email effectiveness.",
          icon: LineChart,
        },
        {
          title: "Transparent Reporting",
          description:
            "Understand campaign performance through clear reporting on engagement, clicks, conversions, and growth opportunities.",
          icon: BarChart3,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "Email marketing strategies tailored to different customer journeys, buying cycles, communication requirements, and business models.",
      items: [
        {
          title: "E-Commerce & Retail",
          description:
            "Increase customer retention and sales through abandoned cart, product, promotional, and lifecycle campaigns.",
          icon: TrendingUp,
        },
        {
          title: "Technology & SaaS",
          description:
            "Nurture leads, onboard users, communicate product updates, and improve customer engagement through automated journeys.",
          icon: Bot,
        },
        {
          title: "Real Estate",
          description:
            "Nurture property enquiries and keep prospects engaged with relevant listings, updates, and follow-up communication.",
          icon: Globe,
        },
        {
          title: "Education",
          description:
            "Manage enquiry nurturing, admissions communication, student engagement, and institutional updates.",
          icon: MessageCircle,
        },
        {
          title: "Healthcare",
          description:
            "Support appropriate patient communication, educational campaigns, reminders, and relationship-building initiatives.",
          icon: ShieldCheck,
        },
        {
          title: "Professional Services",
          description:
            "Nurture business prospects and maintain client relationships through targeted content and automated follow-ups.",
          icon: Target,
        },
      ],
    },

    portfolio: {
      title: "Email Marketing Success Stories",
      description:
        "Email marketing initiatives focused on stronger engagement, customer nurturing, automation, and conversion performance.",
      items: [
        {
          title: "E-Commerce Lifecycle Automation",
          description:
            "Created automated welcome, abandoned cart, post-purchase, and re-engagement journeys designed around customer behaviour.",
          image: "/project-image.webp",
        },
        {
          title: "B2B Lead Nurturing",
          description:
            "Developed segmented email sequences to nurture prospects and move qualified leads toward sales conversations.",
          image: "/project-image.webp",
        },
        {
          title: "Customer Re-Engagement Campaign",
          description:
            "Built targeted campaigns designed to reconnect with inactive subscribers and restore customer engagement.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Email Marketing Experts",
      description:
        "Partner with our email marketing specialists to build targeted campaigns, automated customer journeys, audience segments, and measurable communication strategies that support acquisition and retention.",
      cta: "Let's Grow With Email Marketing",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to create structured email campaigns and automation strategies focused on customer engagement and growth.",
      items: [
        {
          name: "Liam Harris",
          company: "CommerceFlow",
          location: "United Kingdom",
          review:
            "The team helped us organize our email marketing into proper customer journeys instead of isolated campaigns. Our communication is now much more targeted and consistent.",
        },
        {
          name: "Grace Thompson",
          company: "CloudBridge",
          location: "United States",
          review:
            "Skand Tech Hub created automated nurturing flows that made our follow-up process significantly more efficient and gave us much better visibility into campaign performance.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our Email Marketing services.",
      items: [
        {
          question: "What is included in your email marketing services?",
          answer:
            "Our services can include email strategy, campaign creation, copywriting, audience segmentation, automation workflows, personalization, testing, analytics, and ongoing campaign optimization.",
        },
        {
          question: "Can you create automated email sequences?",
          answer:
            "Yes. We can build automated journeys such as welcome sequences, lead nurturing, abandoned cart emails, customer onboarding, post-purchase communication, and re-engagement campaigns.",
        },
        {
          question: "Which email marketing platforms do you work with?",
          answer:
            "Depending on your existing technology and requirements, we can work with platforms such as Mailchimp, HubSpot, Klaviyo, Brevo, ActiveCampaign, and CRM-connected email systems.",
        },
        {
          question: "Can you segment our email subscribers?",
          answer:
            "Yes. We can segment audiences using available customer information, interests, behaviour, purchase activity, engagement, lifecycle stage, and other relevant data.",
        },
        {
          question: "How do you measure email marketing performance?",
          answer:
            "We evaluate relevant metrics such as delivery, opens, clicks, engagement, conversions, audience behaviour, campaign performance, and business outcomes based on the objectives of each campaign.",
        },
      ],
    },
  },
  // Whatsapp Marketing Services
  {
    title: "WhatsApp Marketing",
    slug: "whatsapp-marketing",
    group: "Digital Marketing Solutions",
    icon: MessageCircle,

    seo: {
      title: "WhatsApp Marketing Services",
      description:
        "Connect with customers directly through professional WhatsApp marketing services covering campaigns, automation, broadcasts, lead nurturing, customer engagement, and conversion-focused messaging.",
      keywords: [
        "WhatsApp Marketing",
        "WhatsApp Marketing Services",
        "WhatsApp Business Marketing",
        "WhatsApp Automation",
        "WhatsApp Business API",
        "WhatsApp Marketing Company",
      ],
    },

    hero: {
      title: "WhatsApp Marketing That Turns Conversations Into Customers",
      description:
        "Engage prospects, nurture leads, and strengthen customer relationships with personalized WhatsApp campaigns, automated conversations, broadcasts, and conversion-focused messaging.",
      highlights: [
        "WhatsApp Campaigns",
        "Marketing Automation",
        "Broadcast Messaging",
        "Lead Nurturing",
      ],
    },

    whyChoose: {
      title: "Why Invest in WhatsApp Marketing?",
      description:
        "WhatsApp gives businesses a direct and conversational channel to communicate with prospects and customers throughout the buying journey.",
      items: [
        {
          title: "Direct Customer Communication",
          description:
            "Reach opted-in customers directly with timely and relevant business communication.",
          icon: MessageCircle,
        },
        {
          title: "Faster Lead Engagement",
          description:
            "Respond to enquiries quickly and move interested prospects toward the next step.",
          icon: TrendingUp,
        },
        {
          title: "Automate Conversations",
          description:
            "Use automated workflows and message templates to handle common customer journeys efficiently.",
          icon: Bot,
        },
        {
          title: "Personalize Customer Journeys",
          description:
            "Deliver relevant communication based on customer interests, actions, and lifecycle stages.",
          icon: Target,
        },
      ],
    },

    services: {
      title: "Our WhatsApp Marketing Services",
      description:
        "End-to-end WhatsApp marketing solutions designed to improve customer communication, lead nurturing, engagement, and conversions.",
      items: [
        {
          title: "WhatsApp Campaign Management",
          description:
            "Plan and execute targeted WhatsApp campaigns aligned with your marketing and customer engagement objectives.",
          icon: Megaphone,
          features: [
            "Campaign Strategy",
            "Message Planning",
            "Audience Segmentation",
            "Campaign Scheduling",
            "Performance Tracking",
          ],
        },
        {
          title: "WhatsApp Automation",
          description:
            "Build automated conversational workflows that help businesses respond and follow up more efficiently.",
          icon: Bot,
          features: [
            "Automated Replies",
            "Lead Follow-Ups",
            "Welcome Messages",
            "Customer Journeys",
            "Workflow Automation",
          ],
        },
        {
          title: "Broadcast Campaigns",
          description:
            "Send relevant updates, promotions, announcements, and offers to appropriate opted-in audience segments.",
          icon: MessageCircle,
          features: [
            "Promotional Broadcasts",
            "Product Updates",
            "Offer Campaigns",
            "Customer Announcements",
            "Segmented Messaging",
          ],
        },
        {
          title: "Lead Nurturing & Follow-Up",
          description:
            "Build structured WhatsApp communication journeys that move prospects from initial interest toward conversion.",
          icon: Target,
          features: [
            "Lead Qualification",
            "Follow-Up Sequences",
            "Sales Communication",
            "Reminder Messages",
            "Conversion Journeys",
          ],
        },
      ],
    },

    features: {
      title: "Key Features",
      description:
        "Everything required to create structured, personalized, automated, and measurable WhatsApp marketing journeys.",
      items: [
        {
          title: "Automated Messaging",
          description:
            "Trigger relevant messages and responses based on customer actions and predefined workflows.",
          icon: Bot,
        },
        {
          title: "Audience Segmentation",
          description:
            "Organize contacts into relevant groups for more targeted and personalized communication.",
          icon: Target,
        },
        {
          title: "Campaign Messaging",
          description:
            "Create clear and engaging messages designed around customer needs and campaign objectives.",
          icon: MessageCircle,
        },
        {
          title: "Performance Tracking",
          description:
            "Monitor campaign and conversation performance to identify opportunities for continuous improvement.",
          icon: LineChart,
        },
      ],
    },

    process: {
      title: "Our WhatsApp Marketing Process",
      description:
        "A structured approach focused on permission-based communication, automation, customer engagement, and measurable business outcomes.",
      items: [
        {
          title: "Business & Journey Analysis",
          description:
            "Understand your audience, customer journey, sales process, communication requirements, and campaign objectives.",
        },
        {
          title: "WhatsApp Strategy",
          description:
            "Define campaign types, audience segments, messaging, automation opportunities, and conversion goals.",
        },
        {
          title: "Platform & Workflow Setup",
          description:
            "Configure the required WhatsApp Business tools, integrations, templates, triggers, and automated workflows.",
        },
        {
          title: "Campaign Creation",
          description:
            "Develop campaign messaging, audience segments, broadcasts, follow-ups, and conversational journeys.",
        },
        {
          title: "Campaign Execution",
          description:
            "Launch approved campaigns and automated communication flows for relevant opted-in audiences.",
        },
        {
          title: "Analysis & Optimization",
          description:
            "Measure engagement and campaign outcomes while continuously improving messaging, segmentation, and workflows.",
        },
      ],
    },

    benefits: {
      title: "Benefits of WhatsApp Marketing",
      description:
        "Create faster, more personalized customer communication while improving lead nurturing and engagement.",
      items: [
        {
          title: "Direct Customer Reach",
          description:
            "Communicate with opted-in customers through a familiar channel they already use regularly.",
          icon: MessageCircle,
        },
        {
          title: "Faster Lead Follow-Up",
          description:
            "Respond quickly to new enquiries and maintain momentum throughout the customer journey.",
          icon: TrendingUp,
        },
        {
          title: "Personalized Communication",
          description:
            "Deliver more relevant messages based on customer segments, interests, and previous interactions.",
          icon: Target,
        },
        {
          title: "Automated Customer Journeys",
          description:
            "Reduce repetitive manual communication through structured automated workflows.",
          icon: Bot,
        },
        {
          title: "Improved Customer Engagement",
          description:
            "Build stronger relationships through timely updates, reminders, support, and relevant communication.",
          icon: ShieldCheck,
        },
        {
          title: "Scalable Communication",
          description:
            "Manage growing customer conversations and campaigns with structured messaging and automation.",
          icon: BarChart3,
        },
      ],
    },

    techStack: {
      title: "WhatsApp Marketing Platforms & Tools",
      description:
        "Communication, automation, CRM, and analytics technologies used to build WhatsApp marketing workflows.",
      technologies: [
        "WhatsApp Business",
        "WhatsApp Business API",
        "Meta Business Suite",
        "CRM",
        "Automation",
        "Webhooks",
        "Google Analytics",
        "Google Tag Manager",
      ],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We combine marketing strategy, automation, technology integration, and customer journey planning to build WhatsApp communication around meaningful business outcomes.",
      items: [
        {
          title: "Journey-Based Strategy",
          description:
            "Build WhatsApp campaigns around real customer journeys instead of sending disconnected messages.",
          icon: Target,
        },
        {
          title: "Automation Expertise",
          description:
            "Create automated workflows that improve response times and reduce repetitive communication.",
          icon: Bot,
        },
        {
          title: "Personalized Messaging",
          description:
            "Structure communication around audience segments, customer actions, and campaign objectives.",
          icon: MessageCircle,
        },
        {
          title: "Technology Integration",
          description:
            "Connect WhatsApp communication with CRM systems, lead workflows, websites, and other business tools where required.",
          icon: Globe,
        },
        {
          title: "Performance Optimization",
          description:
            "Analyze campaign and conversation performance to continuously improve customer journeys.",
          icon: LineChart,
        },
        {
          title: "Scalable Solutions",
          description:
            "Build structured communication systems capable of supporting increasing customer and campaign volumes.",
          icon: TrendingUp,
        },
      ],
    },

    industrySolutions: {
      title: "Industries We Serve",
      description:
        "WhatsApp marketing solutions tailored to different customer journeys, communication requirements, and business models.",
      items: [
        {
          title: "E-Commerce & Retail",
          description:
            "Send product updates, order communication, promotional campaigns, abandoned cart reminders, and customer follow-ups.",
          icon: TrendingUp,
        },
        {
          title: "Real Estate",
          description:
            "Nurture property enquiries, share project information, schedule follow-ups, and maintain buyer communication.",
          icon: Globe,
        },
        {
          title: "Education",
          description:
            "Manage student enquiries, admissions follow-ups, reminders, updates, and institutional communication.",
          icon: MessageCircle,
        },
        {
          title: "Healthcare",
          description:
            "Support appropriate appointment communication, reminders, informational updates, and patient engagement workflows.",
          icon: ShieldCheck,
        },
        {
          title: "Hospitality & Travel",
          description:
            "Communicate booking updates, offers, reminders, travel information, and customer assistance.",
          icon: Target,
        },
        {
          title: "Professional Services",
          description:
            "Nurture enquiries and streamline follow-ups, appointment communication, and customer relationship workflows.",
          icon: Bot,
        },
      ],
    },

    portfolio: {
      title: "WhatsApp Marketing Success Stories",
      description:
        "WhatsApp marketing initiatives focused on faster communication, structured lead nurturing, and stronger customer engagement.",
      items: [
        {
          title: "E-Commerce Customer Journey",
          description:
            "Created structured WhatsApp workflows for product communication, customer follow-ups, promotional campaigns, and post-purchase engagement.",
          image: "/project-image.webp",
        },
        {
          title: "Real Estate Lead Nurturing",
          description:
            "Built automated follow-up journeys to help sales teams manage property enquiries and maintain consistent prospect communication.",
          image: "/project-image.webp",
        },
        {
          title: "Service Business Automation",
          description:
            "Developed WhatsApp workflows for enquiry responses, appointment communication, reminders, and customer follow-ups.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire WhatsApp Marketing Experts",
      description:
        "Partner with our WhatsApp marketing specialists to build targeted campaigns, automated conversations, lead nurturing workflows, and scalable customer communication systems.",
      cta: "Let's Build Your WhatsApp Strategy",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to create structured WhatsApp communication and automation strategies for customer engagement.",
      items: [
        {
          name: "Lucas Bennett",
          company: "RetailConnect",
          location: "United Kingdom",
          review:
            "The WhatsApp workflows made our customer follow-up process much more organized. Our team can now manage enquiries and communication far more efficiently.",
        },
        {
          name: "Mia Roberts",
          company: "PropertyLane",
          location: "Australia",
          review:
            "Skand Tech Hub helped us structure our WhatsApp lead journey from initial enquiry through follow-up. It has made communication much easier for our sales team.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our WhatsApp Marketing services.",
      items: [
        {
          question: "What is WhatsApp marketing?",
          answer:
            "WhatsApp marketing uses WhatsApp as a business communication channel for opted-in customer campaigns, lead nurturing, updates, follow-ups, automated journeys, and other relevant customer interactions.",
        },
        {
          question: "Can you automate WhatsApp messages?",
          answer:
            "Yes. Depending on your setup, we can create automated replies, lead follow-ups, reminders, welcome journeys, customer workflows, and other trigger-based communication.",
        },
        {
          question: "Can WhatsApp integrate with our CRM?",
          answer:
            "Yes. Integration possibilities depend on your CRM and WhatsApp setup, but WhatsApp Business API solutions can be connected with CRM systems and other business workflows.",
        },
        {
          question: "Can we send promotional WhatsApp broadcasts?",
          answer:
            "WhatsApp can support business-initiated marketing communication subject to applicable platform requirements, approved templates, customer opt-in, and messaging policies.",
        },
        {
          question: "Do you provide WhatsApp campaign reporting?",
          answer:
            "Yes. Depending on the platform and integration, reporting can cover message delivery, engagement, campaign activity, customer responses, lead journeys, and relevant conversion outcomes.",
        },
      ],
    },
  },
];
